import React from 'react';

import { makeStyles } from 'tss-react/mui';

import {
  getListingCardPriceInformation,
  type ListingCardPriceInformation,
} from '@ha/core/Listing/getListingCardPriceInformation';
import { CurrencyFormat } from '@ha/intl';
import { LocalDate } from '@ha/utilities/Dates/LocalDate';
import { Highlight, Typography } from '@hbf/dsl/core';

import { assertNever } from 'ha/utilities/typescript/assertNever';

import { useIntl } from 'ha/i18n';
import { ListingCardModel } from 'ha/models/Listing/types';

import { SearchFiltersContext } from 'ha/pages/Search/SearchFiltersContext';

import { getListingCardRentReduction } from '../utilities/getListingCardRentReduction';
import { toListingCardPriceListing } from '../utilities/toListingCardPriceListing';

export type PriceInformation = ListingCardPriceInformation;

interface UsePriceInformationParams {
  startDate: LocalDate;
  endDate: LocalDate | null;
  listing: ListingCardModel | null;
}

export function usePriceInformation({
  startDate,
  endDate,
  listing,
}: UsePriceInformationParams): PriceInformation | null {
  const { localState } = React.useContext(SearchFiltersContext) || {};

  return getListingCardPriceInformation({
    listing: listing ? toListingCardPriceListing(listing) : null,
    dateRange: {
      from: startDate.toDate(),
      to: endDate?.toDate(),
    },
    categories: localState?.categories,
  });
}

const useStyles = makeStyles()(theme => ({
  container: {
    display: '-webkit-box',
    WebkitLineClamp: '2',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  // Inline flow spaces items by margin so the line can break between them.
  item: {
    marginRight: theme.utils.spacing('ref/spacing/1'),
  },

  price: {
    whiteSpace: 'nowrap',
  },

  reducedPrice: {
    // Highlight size="md" ships body/sm-semibold (14/24); Figma wants body/md-semibold (16/26).
    '&&': {
      height: theme.typography['body/md-semibold'].lineHeight,
      minHeight: theme.typography['body/md-semibold'].lineHeight,
      paddingInline: theme.utils.spacing('ref/spacing/1'),
      borderRadius: theme.utils.borderRadius('ref/radius/xs'),
    },
    '&& > div': {
      ...theme.typography['body/md-semibold'],
    },
  },

  previousPrice: {
    whiteSpace: 'nowrap',
    textDecoration: 'line-through',
  },
}));

export const ListingCardDetailsPrice = React.memo(
  ({
    priceInformation,
    listing,
    className,
  }: {
    priceInformation: PriceInformation | null;
    listing: ListingCardModel;
    className?: string;
  }) => {
    const { classes, cx } = useStyles();
    const { formatCurrency, formatCurrencyRange, T } = useIntl();

    const currencyFormat = ['EUR', 'USD'].includes(listing.pricing.currency)
      ? CurrencyFormat.WITH_CURRENCY_SYMBOL_NO_FRACTION_NO_GROUPING
      : CurrencyFormat.WITH_CURRENCY_CODE_NO_FRACTION_NO_GROUPING;

    const formatFlatPrice = (price: number) => Math.floor(price).toString();

    const rentReduction = React.useMemo(
      () => getListingCardRentReduction({ listing, priceInformation }),
      [listing, priceInformation],
    );

    const formattedPreviousPrice = React.useMemo(() => {
      if (!rentReduction) return null;

      return formatCurrency(
        rentReduction.previousPrice * 100,
        listing.pricing.currency,
        currencyFormat,
      );
    }, [
      rentReduction,
      formatCurrency,
      listing.pricing.currency,
      currencyFormat,
    ]);

    const formattedPrice = React.useMemo(() => {
      if (!priceInformation) return T('Unknown');

      if (priceInformation.type === 'value') {
        return formatCurrency(
          priceInformation.value * 100,
          listing.pricing.currency,
          currencyFormat,
        );
      }

      if (priceInformation.type === 'range') {
        return formatCurrencyRange(
          priceInformation.range.minPrice * 100,
          priceInformation.range.maxPrice * 100,
          listing.pricing.currency,
          currencyFormat,
        );
      }

      assertNever(priceInformation);
    }, [
      T,
      priceInformation,
      formatCurrency,
      listing.pricing.currency,
      currencyFormat,
      formatCurrencyRange,
    ]);

    const labelPerMonth = React.useMemo(() => {
      if (listing.pricing.utilities === 'P') {
        return T('listing_card.month_incl_some_utilities');
      }
      if (listing.pricing.utilities === 'I') {
        return T('listing_card.month_incl_utilities');
      }
      if (listing.pricing.utilities === 'E') {
        return T('listing_card.month_excl_utilities');
      }

      return T('per_month_symbol');
    }, [listing.pricing.utilities, T]);

    return (
      <div className={classes.container} title={formattedPrice + labelPerMonth}>
        {priceInformation ? (
          <React.Fragment>
            <meta itemProp="priceCurrency" content={listing.pricing.currency} />
            <meta
              itemProp="price"
              content={formatFlatPrice(
                priceInformation.type === 'value'
                  ? priceInformation.value
                  : priceInformation.range.minPrice,
              )}
            />

            {priceInformation.type === 'value' &&
              priceInformation.isMinimum && (
                <Typography
                  variant="body/sm-regular"
                  color="secondary.light"
                  className={classes.item}
                >
                  {T('from_price')}
                </Typography>
              )}

            {rentReduction ? (
              <Highlight
                type="square"
                color="tertiary"
                size="md"
                className={cx(classes.item, classes.reducedPrice, className)}
                data-test-locator="ListingCard/Price"
              >
                {formattedPrice}
              </Highlight>
            ) : (
              <Typography
                variant="body/md-semibold"
                className={cx(classes.item, classes.price, className)}
                data-test-locator="ListingCard/Price"
              >
                {formattedPrice}
              </Typography>
            )}

            {formattedPreviousPrice && (
              <Typography
                variant="body/md-regular"
                color="secondary.light"
                className={cx(classes.item, classes.previousPrice, className)}
                data-test-locator="ListingCard/PreviousPrice"
              >
                {formattedPreviousPrice}
              </Typography>
            )}

            <Typography
              variant="body/sm-regular"
              color="secondary.light"
              className={className}
              data-test-locator="ListingCard/PriceLabel"
            >
              {labelPerMonth}
            </Typography>
          </React.Fragment>
        ) : (
          <Typography variant="body/sm-regular">{T('Unknown')}</Typography>
        )}
      </div>
    );
  },
);
